import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { getIconComponent } from '../components/BuilderIconPickerModal';

const FRAMES = ['none', 'circle', 'sketch', 'organic', 'wave'];
const BORDERS = ['none', 'solid', 'dashed', 'dotted', 'double'];
const ORGANIC_PRESET = {
  rTLh: 255,
  rTRh: 15,
  rBRh: 225,
  rBLh: 15,
  rTLv: 15,
  rTRv: 225,
  rBRv: 15,
  rBLv: 255,
};

export const COUNT_UP_WAVE_PATH = 'M36,22 C62,6 78,30 108,16 C142,2 158,28 196,14 C228,2 248,26 274,18 C286,22 286,40 288,54 C296,84 278,108 288,136 C286,156 262,170 232,164 C190,174 156,150 118,166 C78,182 42,160 24,140 C6,112 18,78 12,50 C8,34 16,28 36,22 Z';

export function createDefaultCountUpContent(copy = {}) {
  return {
    value: 1200,
    prefix: '',
    suffix: '+',
    decimals: 0,
    duration: 1200,
    label: copy.label || 'Alunos',
    caption: copy.caption || '',
    layout: 'stack',
    align: 'center',
    numberSize: 48,
    labelSize: 16,
    numberColor: '#0f172a',
    textColor: '#64748b',
    cardBg: '#ffffff',
    accent: '#2563eb',
    radius: 16,
    frame: 'none',
    borderStyle: 'none',
    borderWidth: 0,
    borderColor: '#0f172a',
    icon: '',
    iconSize: 32,
    iconColor: '#2563eb',
  };
}

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function colorOr(value, fallback) {
  const raw = String(value || '').trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return raw;
  return fallback;
}

function clampInt(value, min, max, fallback) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function organicRadiusCss(content) {
  const keys = ['rTLh', 'rTRh', 'rBRh', 'rBLh', 'rTLv', 'rTRv', 'rBRv', 'rBLv'];
  const hasCustom = keys.some((key) => parseInt(content?.[key], 10) > 0);
  const src = hasCustom ? content : ORGANIC_PRESET;
  const n = (key) => Math.max(0, Math.min(500, parseInt(src?.[key], 10) || 0));
  return `${n('rTLh')}px ${n('rTRh')}px ${n('rBRh')}px ${n('rBLh')}px / ${n('rTLv')}px ${n('rTRv')}px ${n('rBRv')}px ${n('rBLv')}px`;
}

export function countUpView(content) {
  const c = content || {};
  const value = Number(c.value);
  const safeValue = Number.isFinite(value) ? value : 0;
  const decimals = Math.max(0, Math.min(2, parseInt(c.decimals, 10) || 0));
  const duration = Math.max(400, Math.min(2500, parseInt(c.duration, 10) || 1200));
  const layout = c.layout === 'inline' || c.layout === 'card' ? c.layout : 'stack';
  const align = c.align === 'left' ? 'left' : 'center';
  const numberSize = Math.max(28, Math.min(80, parseInt(c.numberSize, 10) || 48));
  const labelSize = Math.max(12, Math.min(28, parseInt(c.labelSize, 10) || 16));
  const radiusRaw = parseInt(c.radius, 10);
  const radius = Number.isFinite(radiusRaw) ? Math.max(0, Math.min(240, radiusRaw)) : 16;
  const frame = FRAMES.includes(c.frame) ? c.frame : 'none';
  const borderChoice = BORDERS.includes(c.borderStyle) ? c.borderStyle : 'none';
  let borderStyle = borderChoice;
  let borderWidth = clampInt(c.borderWidth, 0, 16, 0);
  if (frame === 'sketch') {
    if (borderStyle === 'none') borderStyle = 'solid';
    if (borderWidth < 2) borderWidth = 2;
  }
  if (frame === 'wave' && borderWidth < 2) borderWidth = 2;
  if (borderStyle === 'double' && borderWidth > 0 && borderWidth < 3) borderWidth = 3;
  const iconName = String(c.icon || '').trim();
  const iconSize = clampInt(c.iconSize, 16, 72, 32);
  return {
    value: safeValue,
    prefix: String(c.prefix || '').slice(0, 12),
    suffix: String(c.suffix || '').slice(0, 12),
    decimals,
    duration,
    label: String(c.label || '').slice(0, 80),
    caption: String(c.caption || '').slice(0, 160),
    layout,
    align,
    numberSize,
    labelSize,
    numberColor: colorOr(c.numberColor, '#0f172a'),
    textColor: colorOr(c.textColor, '#64748b'),
    cardBg: colorOr(c.cardBg, '#ffffff'),
    accent: colorOr(c.accent, '#2563eb'),
    radius,
    frame,
    borderChoice,
    borderStyle,
    borderWidth,
    borderColor: colorOr(c.borderColor, '#0f172a'),
    showBorder: frame !== 'wave' && borderWidth > 0 && borderStyle !== 'none',
    organicRadius: organicRadiusCss(c),
    circleSize: Math.max(168, numberSize + (iconName ? iconSize : 0) + 88),
    icon: iconName && iconName !== 'default' ? iconName.slice(0, 80) : '',
    iconSize,
    iconColor: colorOr(c.iconColor, '#2563eb'),
  };
}

export function formatCount(n, decimals, prefix, suffix) {
  const num = Number(n);
  const safe = Number.isFinite(num) ? num : 0;
  let body = safe.toFixed(decimals);
  try {
    body = safe.toLocaleString(typeof document !== 'undefined' ? (document.documentElement.lang || undefined) : undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch (e) {
    body = safe.toFixed(decimals);
  }
  return `${prefix || ''}${body}${suffix || ''}`;
}

function waveDash(style) {
  if (style === 'dashed') return '10 7';
  if (style === 'dotted') return '2 6';
  return '';
}

export function countUpWaveMarkup(view) {
  const dash = waveDash(view.borderChoice);
  const dashAttr = dash ? ` stroke-dasharray="${dash}"` : '';
  return `<svg class="madri-count__blob" viewBox="0 0 300 180" preserveAspectRatio="none" aria-hidden="true"><path vector-effect="non-scaling-stroke" fill="${view.cardBg}" stroke="${view.borderColor}" stroke-width="${view.borderWidth}"${dashAttr} d="${COUNT_UP_WAVE_PATH}"/></svg>`;
}

export function countUpIconMarkup(view) {
  if (!view.icon) return '';
  const IconComp = getIconComponent(view.icon);
  if (!IconComp) return '';
  try {
    const svg = renderToStaticMarkup(createElement(IconComp, { size: view.iconSize, color: view.iconColor }));
    return `<div class="madri-count__icon">${svg}</div>`;
  } catch (e) {
    return '';
  }
}

export function countUpClassName(view) {
  const classes = ['madri-count', `madri-count--${view.layout}`, `madri-count--${view.align}`];
  if (view.frame !== 'none') classes.push('madri-count--framed', `madri-count--${view.frame}`);
  if (view.showBorder) classes.push('madri-count--boxed');
  return classes.join(' ');
}

export function countUpCssVars(view) {
  return [
    `--mc-num:${view.numberColor}`,
    `--mc-text:${view.textColor}`,
    `--mc-card:${view.cardBg}`,
    `--mc-accent:${view.accent}`,
    `--mc-size:${view.numberSize}px`,
    `--mc-label:${view.labelSize}px`,
    `--mc-radius:${view.radius}px`,
    `--mc-border:${view.borderColor}`,
    `--mc-bw:${view.borderWidth}px`,
    `--mc-bs:${view.borderStyle}`,
    `--mc-organic:${view.organicRadius}`,
    `--mc-icon:${view.iconColor}`,
    `--mc-icon-size:${view.iconSize}px`,
    `--mc-circle:${view.circleSize}px`,
  ].join(';');
}

export function buildCountUpInnerHtml(content) {
  const view = countUpView(content);
  const label = view.label ? `<div class="madri-count__label">${esc(view.label)}</div>` : '';
  const caption = view.caption ? `<div class="madri-count__caption">${esc(view.caption)}</div>` : '';
  const texts = (label || caption) ? `<div class="madri-count__copy">${label}${caption}</div>` : '';
  const zero = esc(formatCount(0, view.decimals, view.prefix, view.suffix));
  const wave = view.frame === 'wave' ? countUpWaveMarkup(view) : '';
  const icon = countUpIconMarkup(view);
  return `<style>${COUNT_UP_CSS}</style><div class="${countUpClassName(view)}" style="${countUpCssVars(view)}">${wave}${icon}<div class="madri-count__value" data-count-to="${view.value}" data-count-dec="${view.decimals}" data-count-ms="${view.duration}" data-count-prefix="${esc(view.prefix)}" data-count-suffix="${esc(view.suffix)}">${zero}</div>${texts}</div><script>(function(){var s=document.currentScript;var root=s&&s.previousElementSibling;if(!root)return;var node=root.querySelector("[data-count-to]");if(!node)return;var to=parseFloat(node.getAttribute("data-count-to"));if(!isFinite(to))return;var dec=parseInt(node.getAttribute("data-count-dec")||"0",10)||0;var ms=parseInt(node.getAttribute("data-count-ms")||"1200",10)||1200;var prefix=node.getAttribute("data-count-prefix")||"";var suffix=node.getAttribute("data-count-suffix")||"";function fmt(n){var body;try{body=n.toLocaleString(document.documentElement.lang||undefined,{minimumFractionDigits:dec,maximumFractionDigits:dec});}catch(e){body=n.toFixed(dec);}return prefix+body+suffix;}function run(){var t0=performance.now();function step(now){var p=Math.min(1,(now-t0)/ms);var eased=1-Math.pow(1-p,3);node.textContent=fmt(to*eased);if(p<1)requestAnimationFrame(step);}requestAnimationFrame(step);}if(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches){node.textContent=fmt(to);return;}if(!("IntersectionObserver"in window)){run();return;}var io=new IntersectionObserver(function(list){list.forEach(function(en){if(en.isIntersecting){io.disconnect();run();}});},{threshold:0.45});io.observe(root);})();</script>`;
}

export const COUNT_UP_CSS = `
.madri-count{display:flex;flex-direction:column;gap:6px;box-sizing:border-box;width:100%}
.madri-count--center{align-items:center;text-align:center}
.madri-count--left{align-items:flex-start;text-align:left}
.madri-count--inline{flex-direction:row;align-items:center;gap:16px}
.madri-count--inline.madri-count--center{justify-content:center}
.madri-count--card{background:var(--mc-card,#fff);border-radius:var(--mc-radius,16px);padding:22px 18px;border-top:4px solid var(--mc-accent,#2563eb);box-shadow:0 10px 28px rgba(15,23,42,.08)}
.madri-count__value{font-weight:800;line-height:1;font-size:var(--mc-size,48px);color:var(--mc-num,#0f172a) !important;font-variant-numeric:tabular-nums}
.madri-count__label{font-weight:700;font-size:var(--mc-label,16px);line-height:1.3;color:var(--mc-text,#64748b) !important}
.madri-count__caption{font-size:13px;line-height:1.45;color:var(--mc-text,#64748b) !important;opacity:.8}
.madri-count__copy{display:flex;flex-direction:column;gap:2px;min-width:0}
.madri-count__icon{display:flex;flex-shrink:0;line-height:0;color:var(--mc-icon,#2563eb)}
.madri-count__icon svg{width:var(--mc-icon-size,32px);height:var(--mc-icon-size,32px)}
.madri-count--framed{position:relative;background:var(--mc-card,#fff);padding:22px 18px;border-radius:var(--mc-radius,16px);box-shadow:0 10px 28px rgba(15,23,42,.08)}
.madri-count--boxed{border:var(--mc-bw,1px) var(--mc-bs,solid) var(--mc-border,#0f172a);border-radius:var(--mc-radius,16px);padding:18px 16px}
.madri-count--circle{border-radius:50% !important;aspect-ratio:1;width:min(100%,var(--mc-circle,220px));justify-content:center;align-items:center !important;text-align:center;margin-inline:auto;overflow:hidden}
.madri-count--circle.madri-count--left{margin-left:0;margin-right:auto}
.madri-count--circle.madri-count--inline{flex-direction:column}
.madri-count--sketch{border-radius:255px 15px 225px 15px / 15px 225px 15px 255px !important;box-shadow:3px 4px 0 var(--mc-border,#0f172a)}
.madri-count--organic{border-radius:var(--mc-organic,255px 15px 225px 15px / 15px 225px 15px 255px) !important}
.madri-count--wave{background:transparent !important;box-shadow:none !important;border:none !important;border-radius:0 !important;padding:34px 28px}
.madri-count--wave .madri-count__blob{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:0}
.madri-count--wave .madri-count__value,.madri-count--wave .madri-count__copy,.madri-count--wave .madri-count__icon{position:relative;z-index:1}
.madri-count--circle.madri-count--card,.madri-count--sketch.madri-count--card,.madri-count--organic.madri-count--card,.madri-count--wave.madri-count--card{border-top:none}
`;
